// Переключатель представлений реестра. Одно представление — одна грамматика строки
// (правило 2), а здесь ещё и счётчик у каждого: «Штат 184», «Договор 63».
//
// Счётчики приходят с сервера тем же срезом, что и список, и это важнее, чем кажется: если
// считать их на клиенте по показанной странице, у человека с урезанной областью видимости
// цифры разойдутся с его же списком, и он решит, что часть людей потерялась.
//
// Пока счётчики едут, кнопки уже стоят на месте с прежними числами (keepPreviousData) —
// спиннер на месте показанного числа запрещён (правило 11).

// Полоса вкладок, а не сегментированный переключатель. Правка 03.09.2026, общая на весь продукт.
//
// Сегментированный раскладывает доли РАВНЫМИ и запрещает перенос текста: как только пунктов
// становится больше, чем помещается, названия налезают друг на друга. В «Найме» это уже
// случилось — восемь названий в колонке 416 точек рисовались одно поверх другого, — а в
// «Деньгах» стояло в шаге от этого: «Очередь приёмки» требовала 115 точек при доле в 106.
//
// Граница теперь по смыслу, а не по числу пунктов: сегментированный остаётся там, где человек
// выбирает РЕЖИМ из двух-трёх равных (порядок сортировки на пульте, способ ведения табеля);
// переход между ПРЕДСТАВЛЕНИЯМИ раздела везде идёт полосой вкладок. У неё ширина по
// содержимому и прокрутка, то есть наложение невозможно по устройству. Держит
// `e2e/переключатель-читается.spec.ts` — замером координат, а не чтением исходника: пункты
// собираются циклом по правам, и в тексте файла их не пересчитать.

import { useNavigate } from 'react-router';

import * as TabMenu from '@/shared/ui/tab-menu-horizontal';

import { useEmploymentViewsQuery } from '../queries';
import type { PeopleRoute, PeopleView } from '../route';
import { VIEW_NO_TYPE, sameView, switchViewHref, viewSlug } from '../route';

export function ViewSwitcher({ route }: { route: PeopleRoute }) {
  const navigate = useNavigate();
  const views = useEmploymentViewsQuery();

  const items: { view: PeopleView; title: string; count: number | null }[] = [
    { view: { kind: 'all' }, title: 'Все', count: views.data?.total ?? null },
    ...(views.data?.items ?? []).map((item) => ({
      view: { kind: 'employment' as const, typeId: item.employment_type_id },
      title: item.title,
      count: item.count,
    })),
  ];

  // Переключатель, который не может ничего изменить, не показывается вовсе.
  //
  // На живых данных заказчика тип оформления не заполнен НИ У КОГО, и переключатель выходил
  // такой: «Все 286 | Без типа оформления 286». Два выбора с одинаковым числом читаются как
  // сломанный отбор — человек жмёт и не понимает, почему список не изменился. Сам факт «тип
  // не заполнен ни у кого» при этом виден там, где ему место: в карточке человека и в отборе.
  const срезМеняетСписок = items.some(
    (item) =>
      item.view.kind !== 'all' && item.count !== null && item.count < (views.data?.total ?? 0),
  );
  const выбранЧужойСрез = !items.some((item) => sameView(item.view, route.view));
  if (!срезМеняетСписок && !выбранЧужойСрез) {
    return null;
  }

  return (
    <TabMenu.Root
      value={viewSlug(route.view)}
      onValueChange={(slug) => {
        const target = items.find((item) => viewSlug(item.view) === slug);
        if (target !== undefined) {
          navigate(switchViewHref(target.view, route.search));
        }
      }}
    >
      <TabMenu.List className='h-10 gap-5 border-t-0'>
        {items.map((item) => (
          <TabMenu.Trigger key={viewSlug(item.view)} value={viewSlug(item.view)}>
            {item.title}
            {item.count !== null && <span className='ml-1 text-text-sub-600'>{item.count}</span>}
          </TabMenu.Trigger>
        ))}
        {/* Открытое по прямой ссылке представление, которого нет в ответе сервера (тип
            оформления удалили или он вне области видимости), обязано остаться в
            переключателе: иначе человек видит список, а активной кнопки нет. */}
        {!items.some((item) => sameView(item.view, route.view)) && (
          <TabMenu.Trigger value={viewSlug(route.view)}>
            {viewSlug(route.view) === VIEW_NO_TYPE ? 'Без типа' : 'Выбранный срез'}
          </TabMenu.Trigger>
        )}
      </TabMenu.List>
    </TabMenu.Root>
  );
}
